import { useRef, useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';

import { uploadImage, validateImageFile } from 'src/utils/upload';

import { useTranslate } from 'src/locales';

import { toast } from 'src/components/snackbar';
import { Iconify } from 'src/components/iconify';

// ----------------------------------------------------------------------

export type PhotoCaptureShape = 'circle' | 'wide' | 'card';

export type PhotoCaptureFieldProps = {
  label: string;
  helper?: string;
  /** Persisted URL from a previous session. Shown until a new photo replaces it. */
  value?: string;
  onChange: (url: string | undefined) => void;
  /** Opens the selfie camera instead of the rear one. */
  frontCamera?: boolean;
  shape?: PhotoCaptureShape;
  /** Surfaces upload state so a parent can disable its submit button. */
  onBusyChange?: (busy: boolean) => void;
  disabled?: boolean;
};

const SHAPE_SX: Record<PhotoCaptureShape, object> = {
  circle: { width: 128, height: 128, borderRadius: '50%', mx: 'auto' },
  wide: { width: '100%', height: 200, borderRadius: 1.5 },
  card: { width: '100%', height: 168, borderRadius: 1.5 },
};

/**
 * One photo, always with a preview.
 *
 * Replaces the two upload paths that used to exist side by side: `UploadBox`,
 * which renders a dropzone and silently ignores its `value` so nothing is ever
 * shown back, and the KYC capture step, which previewed correctly but was
 * hardcoded to that flow. Everything that takes a photo now goes through here.
 *
 * Camera-first: the primary button carries the `capture` attribute so Android
 * hands the user straight to the camera app. That is an intent handoff, so it
 * needs no CAMERA permission of ours - which is why AndroidManifest.xml still
 * deliberately omits it. Gallery is a second, plain input.
 */
export function PhotoCaptureField({
  label,
  helper,
  value,
  onChange,
  frontCamera = false,
  shape = 'wide',
  onBusyChange,
  disabled,
}: PhotoCaptureFieldProps) {
  const { t } = useTranslate('components');

  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const galleryInputRef = useRef<HTMLInputElement | null>(null);

  // Object URL for instant feedback while the real upload is still in flight.
  const objectUrlRef = useRef<string | null>(null);
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [stage, setStage] = useState<'compressing' | 'uploading' | null>(null);

  const busy = progress !== null;
  const previewUrl = localPreview ?? value ?? null;

  const releaseObjectUrl = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  useEffect(() => releaseObjectUrl, [releaseObjectUrl]);

  const handleFile = useCallback(
    async (file: File | undefined) => {
      if (!file) return;

      const validation = validateImageFile(file);
      if (!validation.valid) {
        toast.error(validation.error ?? t('photoCapture.invalidFile'));
        return;
      }

      releaseObjectUrl();
      const preview = URL.createObjectURL(file);
      objectUrlRef.current = preview;
      setLocalPreview(preview);
      setProgress(0);
      setStage('compressing');
      onBusyChange?.(true);

      const result = await uploadImage(file, {
        onProgress: ({ progress: p, stage: s }) => {
          setProgress(Math.round(p));
          setStage(s);
        },
      });

      setProgress(null);
      setStage(null);
      onBusyChange?.(false);

      if ('error' in result) {
        // Drop the preview too - keeping it would imply a photo was saved.
        releaseObjectUrl();
        setLocalPreview(null);
        toast.error(result.error);
        return;
      }

      onChange(result.url);
    },
    [onChange, onBusyChange, releaseObjectUrl, t]
  );

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    void handleFile(file);
  };

  const handleRemove = () => {
    releaseObjectUrl();
    setLocalPreview(null);
    onChange(undefined);
  };

  return (
    <Stack spacing={1.25}>
      <Stack spacing={0.25}>
        <Typography variant="subtitle2">{label}</Typography>
        {helper && (
          <Typography variant="caption" color="text.secondary">
            {helper}
          </Typography>
        )}
      </Stack>

      <Box
        onClick={() => !busy && !disabled && cameraInputRef.current?.click()}
        sx={{
          ...SHAPE_SX[shape],
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.neutral',
          border: (theme) =>
            `1px ${previewUrl ? 'solid' : 'dashed'} ${theme.palette.divider}`,
          cursor: busy || disabled ? 'default' : 'pointer',
          transition: (theme) => theme.transitions.create('border-color'),
          '&:hover': { borderColor: busy || disabled ? undefined : 'primary.main' },
        }}
      >
        {previewUrl ? (
          <Box
            component="img"
            src={previewUrl}
            alt={label}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Stack spacing={0.75} alignItems="center" sx={{ px: 2, textAlign: 'center' }}>
            <Iconify
              icon="solar:camera-add-bold-duotone"
              width={shape === 'circle' ? 32 : 40}
              sx={{ color: 'text.disabled' }}
            />
            {shape !== 'circle' && (
              <Typography variant="caption" color="text.secondary">
                {t('photoCapture.tapToCapture')}
              </Typography>
            )}
          </Stack>
        )}
      </Box>

      {busy && (
        <Stack spacing={0.5}>
          <LinearProgress variant="determinate" value={progress ?? 0} />
          <Typography variant="caption" color="text.secondary">
            {stage === 'compressing'
              ? t('photoCapture.compressing', { progress })
              : t('photoCapture.uploading', { progress })}
          </Typography>
        </Stack>
      )}

      <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap">
        <Button
          size="small"
          variant={previewUrl ? 'outlined' : 'contained'}
          startIcon={<Iconify icon="solar:camera-add-bold" />}
          onClick={() => cameraInputRef.current?.click()}
          disabled={busy || disabled}
        >
          {previewUrl ? t('photoCapture.retake') : t('photoCapture.takePhoto')}
        </Button>

        <Button
          size="small"
          color="inherit"
          startIcon={<Iconify icon="solar:gallery-bold" />}
          onClick={() => galleryInputRef.current?.click()}
          disabled={busy || disabled}
        >
          {t('photoCapture.chooseFromGallery')}
        </Button>

        {previewUrl && (
          <Button size="small" color="error" onClick={handleRemove} disabled={busy || disabled}>
            {t('photoCapture.remove')}
          </Button>
        )}
      </Stack>

      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        {...({ capture: frontCamera ? 'user' : 'environment' } as any)}
        style={{ display: 'none' }}
        onChange={onInputChange}
      />
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={onInputChange}
      />
    </Stack>
  );
}
